import { Bold, Italic, MoreHorizontal } from "lucide-react";
import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";
import type { TLNoteShape } from "@tldraw/tlschema";
import { TextStyleSelect } from "./TextStyleSelect";
import { textHasMark, toggleTextMark } from "./text-format";

const noteColors = [
  ["yellow", "Yellow"],
  ["blue", "Blue"],
  ["green", "Green"],
  ["red", "Red"],
  ["violet", "Violet"],
  ["grey", "Grey"],
  ["white", "White"],
  ["black", "Charcoal"],
] as const;

/** Context controls for a native sticky note; its text remains rich/editable. */
export function StickyFormatting({
  editor,
  shape,
}: {
  editor: Editor;
  shape: TLNoteShape;
}) {
  const editing = useValue(
    "editing sticky",
    () => editor.getEditingShapeId() === shape.id,
    [editor, shape.id],
  );
  const rich = useValue(
    "sticky rich text editor",
    () => editor.getRichTextEditor(),
    [editor],
  );
  const resume = () => {
    if (editing && rich && !rich.isDestroyed) rich.view.focus();
    else editor.focus();
  };
  const update = (props: Partial<TLNoteShape["props"]>) => {
    const current = editor.getShape<TLNoteShape>(shape.id);
    if (!current || current.isLocked || editor.getIsReadonly()) return;
    editor.markHistoryStoppingPoint("Format sticky note");
    editor.updateShape<TLNoteShape>({ id: shape.id, type: "note", props });
    resume();
  };
  const mark = (kind: "bold" | "italic") => {
    if (editing && rich && !rich.isDestroyed) rich.commands.toggleMark(kind);
    else update({ richText: toggleTextMark(shape.props.richText, kind) });
    resume();
  };
  return (
    <div
      className="text-formatting sticky-formatting"
      role="toolbar"
      aria-label="Sticky note formatting"
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => {
        if (event.target instanceof Element && event.target.closest("button"))
          event.preventDefault();
      }}
    >
      <TextStyleSelect
        className="note-fill-control"
        label="Note background"
        title="Sticky note background"
        value={shape.props.color}
        decoration={
          <span
            className={`note-fill-swatch note-fill-${shape.props.color}`}
            aria-hidden="true"
          />
        }
        options={noteColors.map(([value, label]) => ({
          value,
          label,
          color:
            editor.getCurrentTheme().colors[editor.getColorMode()][value]
              ?.solid,
        }))}
        onChange={(value) =>
          update({ color: value as TLNoteShape["props"]["color"] })
        }
        onDismiss={resume}
      />
      <TextStyleSelect
        className="note-size-control"
        label="Note text size"
        title="Sticky note text size"
        value={shape.props.size}
        options={[
          { value: "s", label: "Small" },
          { value: "m", label: "Medium" },
          { value: "l", label: "Large" },
          { value: "xl", label: "Extra large" },
        ]}
        onChange={(value) =>
          update({
            size: value as TLNoteShape["props"]["size"],
            fontSizeAdjustment: null,
          })
        }
        onDismiss={resume}
      />
      <button
        aria-label="Bold note text"
        title="Bold note text"
        aria-pressed={
          editing && rich
            ? rich.isActive("bold")
            : textHasMark(shape.props.richText, "bold")
        }
        onClick={() => mark("bold")}
      >
        <Bold size={16} />
      </button>
      <button
        aria-label="Italic note text"
        title="Italic note text"
        aria-pressed={
          editing && rich
            ? rich.isActive("italic")
            : textHasMark(shape.props.richText, "italic")
        }
        onClick={() => mark("italic")}
      >
        <Italic size={16} />
      </button>
      <details className="format-more note-format-more">
        <summary
          aria-label="More sticky note formatting"
          title="More note formatting"
          onMouseDown={(event) => event.preventDefault()}
        >
          <MoreHorizontal size={18} />
        </summary>
        <div className="format-popover floating-surface">
          <strong className="calque-format-heading">Note text</strong>
          <TextStyleSelect
            label="Note text colour"
            title="Sticky note text colour"
            value={shape.props.labelColor}
            options={[
              { value: "black", label: "Automatic" },
              ...noteColors
                .filter(([value]) => value !== "black")
                .map(([value, label]) => ({ value, label })),
            ]}
            onChange={(value) =>
              update({
                labelColor: value as TLNoteShape["props"]["labelColor"],
              })
            }
            onDismiss={resume}
          />
          <TextStyleSelect
            label="Note font"
            title="Sticky note font"
            value={shape.props.font}
            options={[
              { value: "sans", label: "Sans serif" },
              { value: "serif", label: "Serif" },
              { value: "mono", label: "Monospace" },
              { value: "draw", label: "Handwritten" },
            ]}
            onChange={(value) =>
              update({ font: value as TLNoteShape["props"]["font"] })
            }
            onDismiss={resume}
          />
        </div>
      </details>
    </div>
  );
}
